/**
 * Review and confirm step for deployment wizard Step 4.
 */
import { Link } from 'react-router-dom'
import type { ThesisAllocation } from '../../types/thesis'
import type { DeploymentCandidate } from '../../types/deploy'

interface ReviewConfirmProps {
  thesis: ThesisAllocation | null
  amount: number
  selectedCandidates: DeploymentCandidate[]
}

export function ReviewConfirm({
  thesis,
  amount,
  selectedCandidates,
}: ReviewConfirmProps) {
  // Calculate totals for selected candidates only
  const totalWeight = selectedCandidates.reduce((sum, c) => sum + c.suggested_weight, 0)
  const normalizedCandidates = selectedCandidates.map((c) => ({
    ...c,
    adjustedWeight: totalWeight > 0 ? (c.suggested_weight / totalWeight) * 100 : 0,
    adjustedAmount: totalWeight > 0 ? (c.suggested_weight / totalWeight) * amount : 0,
  }))

  return (
    <div className="max-w-2xl mx-auto">
      {/* Summary card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Deployment Summary</h3>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Thesis</span>
            <span className="font-medium text-gray-900">{thesis?.thesis_name ?? '-'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-500">Total Amount</span>
            <span className="font-medium text-gray-900">
              ${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-500">Candidates Selected</span>
            <span className="font-medium text-gray-900">{selectedCandidates.length}</span>
          </div>
        </div>
      </div>

      {/* Candidate breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Selected Candidates</h3>
          <p className="text-sm text-gray-500 mt-1">
            Click a symbol to open in Buy Simulator with pre-filled values
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {normalizedCandidates.map((candidate) => (
            <div
              key={candidate.symbol}
              className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900">{candidate.symbol}</span>
                  <span className="text-sm text-gray-500">{candidate.name}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {candidate.adjustedWeight.toFixed(0)}% &middot;{' '}
                  ${candidate.adjustedAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </p>
              </div>

              <Link
                to={`/what-if/buy?symbol=${encodeURIComponent(candidate.symbol)}`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
              >
                <SimulatorIcon className="w-4 h-4 mr-2" />
                Simulate Buy
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Help text */}
      <p className="mt-6 text-center text-sm text-gray-500">
        The Buy Simulator lets you see how each purchase would affect your portfolio
        before making a decision.
      </p>
    </div>
  )
}

function SimulatorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  )
}
